import resample from './resample.js'
import advect from './advect.js'

// Stage 03 owns every geometric channel the field can drive.
//
//   displace : value -> z         (height field)
//   shift    : value -> x, y      (in-plane, along `axis`)
//   twist    : base  -> angle     (rotation field)
//
// They are duals — the same scalar routed to position or to orientation — and
// they belong here, not in the field (a scalar over space, which should stay
// dimension-agnostic) and not in the substrate (which owns where we sample,
// not how the sample responds).
//
// `align` picks what the angle is measured from, because three different
// things all want to write the angle slot and only one of them is expensive:
//
//   flow   gradient of the field   marks lie along the flow, like iron filings
//   field  the field value itself  a coherent rotation landscape
//   fixed  nothing                 leaves only spin
//
// Spin is additive on top of any of them. It is the clock reaching the angle
// channel exactly as `rate` reaches the field, which is why it lives in 03 and
// not in the mark.
export const motionParams = [
  { key: 'displace', label: 'displace', tier: 'continuous', min: -1.5, max: 1.5, step: 0.05, default: 0,
    hint: 'field value -> z · needs tilt or perspective in 06 to be visible' },
  // Displacement in the plane of the substrate rather than out of it. A
  // surprising number of chapters are a lattice whose *positions* are pushed
  // around by a wave — rows bending, rings rippling — and with only z to write
  // into, all of that arrived as marks brightening instead of geometry moving.
  { key: 'shift', label: 'shift', tier: 'continuous', min: -1, max: 1, step: 0.01, default: 0,
    hint: 'field value -> x, y' },
  // Which way shift pushes. radial and the fixed axes are free; gradient costs
  // the same four extra samples as flow alignment, and is gated the same way.
  { key: 'axis', label: 'shift axis', tier: 'cosmetic', type: 'enum', default: 'radial',
    options: [
      { value: 'radial', label: 'radial' },
      { value: 'x', label: 'x' },
      { value: 'y', label: 'y' },
      { value: 'gradient', label: 'gradient' },
    ] },
  { key: 'align', label: 'rotation', tier: 'cosmetic', type: 'enum', default: 'flow',
    options: [
      { value: 'flow', label: 'flow' },
      { value: 'field', label: 'field' },
      { value: 'fixed', label: 'fixed' },
    ] },
  { key: 'twist', label: 'twist', tier: 'continuous', min: -4, max: 4, step: 0.05, default: 1,
    hint: 'scales the rotation source · needs tick or polygon marks' },
  // Each mark turning on its own centre. For the whole piece revolving, see
  // frame.orbit in stage 06 — different thing, different stage.
  { key: 'spin', label: 'spin', tier: 'continuous', min: -3, max: 3, step: 0.05, default: 0,
    hint: 'each mark turns in place · radians per unit time' },
]

export const motions = { resample, advect }
